/**
 * WidgetControls — Minimize and close buttons rendered as DOM elements
 * positioned near the avatar.
 */

export class WidgetControls {
  private container: HTMLDivElement | null = null
  private minimizeBtn: HTMLButtonElement | null = null
  private closeBtn: HTMLButtonElement | null = null

  onMinimize: (() => void) | null = null
  onRestore: (() => void) | null = null
  onClose: (() => void) | null = null

  private minimized = false

  /** Show the controls (call when conversation becomes active). */
  show(): void {
    if (this.container) return

    this.container = document.createElement('div')
    Object.assign(this.container.style, {
      position: 'fixed',
      bottom: '8px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '2147483647',
      display: 'flex',
      gap: '6px',
      opacity: '0',
      transition: 'opacity 0.3s ease',
    })

    this.minimizeBtn = this.createButton('\u2013', 'Minimize conversation') // en dash
    this.closeBtn = this.createButton('\u00D7', 'Close conversation')       // multiplication sign

    this.minimizeBtn.addEventListener('click', () => {
      if (this.minimized) {
        this.minimized = false
        this.minimizeBtn!.textContent = '\u2013'
        this.minimizeBtn!.title = 'Minimize'
        this.onRestore?.()
      } else {
        this.minimized = true
        this.minimizeBtn!.textContent = '\u25A1' // small square (restore icon)
        this.minimizeBtn!.title = 'Restore'
        this.onMinimize?.()
      }
    })

    this.closeBtn.addEventListener('click', () => {
      this.onClose?.()
    })

    this.container.appendChild(this.minimizeBtn)
    this.container.appendChild(this.closeBtn)
    document.body.appendChild(this.container)

    // Fade in
    requestAnimationFrame(() => {
      if (this.container) this.container.style.opacity = '1'
    })
  }

  /** Hide and remove controls. */
  hide(): void {
    if (this.container) {
      this.container.remove()
      this.container = null
      this.minimizeBtn = null
      this.closeBtn = null
      this.minimized = false
    }
  }

  private createButton(icon: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.textContent = icon
    btn.title = title
    btn.setAttribute('aria-label', title)
    Object.assign(btn.style, {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      border: '1px solid rgba(200, 208, 218, 0.6)',
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#4a4a6a',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      lineHeight: '1',
      transition: 'background 0.15s, transform 0.15s',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    })
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(240, 242, 245, 0.95)'
      btn.style.transform = 'scale(1.1)'
    })
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(255, 255, 255, 0.85)'
      btn.style.transform = 'scale(1)'
    })
    return btn
  }
}
